import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { BriefResponseDto } from '../../../../../../common/general-types/briefs.dto';
import { VideoAiModerateService } from '../../../../../../services/video-ai-moderate/video-ai-moderate.service';
import { FormatService } from '../../../../../../common/format/format.service';
import { VideoService } from '../../../../../../services/video/video.service';
import { LoadingInlineComponent } from '../../../../../../atoms/loading-inline/loading-inline.component';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

type NoodleQuestionTrends = {
  uniqueContentCount: number;
  trends: {
    approvedCount: number;
    approvedPercentage: number;
    briefId: string;
    issueFoundCount: number;
    issueFoundPercentage: number;
    statement: string;
    totalReviews: number;
  }[];
};

type ApprovedModeratedVideo = {
  deliverableId: number;
  failed: number;
  generalised: boolean;
  id: string;
  passed: number;
  passedPercentage: number;
  questionSubType: 'mandatory' | string;
  questionType: number;
  statement: string;
  subjectiveType: number;
  supplementary: string | null;
  type: 'do' | 'dont';
};

@Component({
  selector: 'mbtr-brief-accuracy',
  imports: [DecimalPipe, LoadingInlineComponent],
  templateUrl: './brief-accuracy.component.html',
  styleUrl: './brief-accuracy.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefAccuracyComponent implements OnInit, OnDestroy {

  @Input() focussedBrief!: BriefResponseDto;

  subscriptionList = new Subscription();

  trendsLoadingState = signal<LoadingState>('idle');
  approvedVideosLoadingState = signal<LoadingState>('idle');

  /** Noodle Question Trends */
  noodleQuestionTrends = signal<NoodleQuestionTrends | undefined>(undefined);
  /** Noodle Question Trends - END */

  /** Approved Moderated Videos */
  approvedModeratedVideos = signal<ApprovedModeratedVideo[] | undefined>(undefined);

  approvalRate = signal<number | undefined>(undefined);
  mostFailedQuestions = signal<{
    id: string;
    question: string;
    percentage: number;
    failed: number;
    passed: number;
    type?: 'do' | 'dont';
  }[] | undefined>(undefined);
  /** Approved Moderated Videos - END */

  /** Services */
  private videoAiModerateService = inject(VideoAiModerateService);
  public formatService = inject(FormatService);
  private videoService = inject(VideoService);
  /** Services - END */

  ngOnInit(): void {
    if (this.focussedBrief) {
      this.getNoodleQuestionTrends();
      this.getApprovedVideoReviews();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getNoodleQuestionTrends(): void {
    this.trendsLoadingState.set('loading');

    this.subscriptionList.add(
      this.videoAiModerateService.getNoodleQuestionTrends(this.focussedBrief.id).subscribe({
        next: (res: unknown) => {
          const data = res as Partial<NoodleQuestionTrends> | null | undefined;
          this.noodleQuestionTrends.set({
            uniqueContentCount: data?.uniqueContentCount ?? 0,
            trends: Array.isArray(data?.trends) ? data.trends : [],
          });
          this.trendsLoadingState.set('loaded');
        },
        error: () => {
          this.trendsLoadingState.set('error');
        },
      }),
    );
  }

  getApprovedVideoReviews(): void {
    this.approvedVideosLoadingState.set('loading');

    this.subscriptionList.add(
      this.videoService.getApprovedAiReviewedVideoForBrief(this.focussedBrief.id).subscribe({
        next: (res: unknown) => {
          const videos = Array.isArray(res) ? res as ApprovedModeratedVideo[] : [];
          this.approvedModeratedVideos.set(videos);
          this.approvalRate.set(this.getApprovalRate());
          this.mostFailedQuestions.set(this.getMostFailedQuestions());
          this.approvedVideosLoadingState.set('loaded');
        },
        error: () => {
          this.approvedVideosLoadingState.set('error');
        },
      }),
    );
  }

  getApprovalRate(): number {
    const totalApproved = this.approvedModeratedVideos()?.reduce((sum, item) => sum + item.passed, 0) || 0;
    const totalReviews = this.approvedModeratedVideos()?.reduce((sum, item) => sum + (item.passed + item.failed), 0) || 0;
    if (totalReviews === 0) {
      return 0;
    }
    return Math.round((totalApproved / totalReviews) * 100 * 10) / 10;
  }

  getMostFailedQuestions() {
    const mostFailedQuestions = [...(this.approvedModeratedVideos() ?? [])].sort((a, b) => b.failed - a.failed);
    const elimateAllPassedQuestions = mostFailedQuestions.filter((question) => question.passedPercentage < 100);

    return elimateAllPassedQuestions.map((question) => {
      const total = question.passed + question.failed;
      const failurePercentage = total > 0 ? Math.round(((question.failed / total) * 100) * 10) / 10 : 0;

      return {
        id: question.id,
        question: question.statement,
        percentage: failurePercentage,
        failed: question.failed,
        passed: question.passed,
        type: question.type,
      };
    });
  }

  getTotalApprovedVideos(): number {
    return this.approvedModeratedVideos()?.reduce((sum, item) => sum + (item.passed + item.failed), 0) || 0;
  }

  getTotalPassedVideos(): number {
    return this.approvedModeratedVideos()?.reduce((sum, item) => sum + item.passed, 0) || 0;
  }

  /** Aggregated Metrics */
  getAggregatedApprovalPercentage(): number {
    if (!this.noodleQuestionTrends() || !this.noodleQuestionTrends()!.trends || this.noodleQuestionTrends()!.trends.length === 0) {
      return 0;
    }
    const trends = this.noodleQuestionTrends()!.trends;
    const totalApproved = trends.reduce((sum, trend) => sum + trend.approvedCount, 0);
    const totalReviews = trends.reduce((sum, trend) => sum + trend.totalReviews, 0);
    return totalReviews > 0 ? (totalApproved / totalReviews) * 100 : 0;
  }

  getAggregatedIssuePercentage(): number {
    if (!this.noodleQuestionTrends() || !this.noodleQuestionTrends()!.trends || this.noodleQuestionTrends()!.trends.length === 0) {
      return 0;
    }
    const trends = this.noodleQuestionTrends()!.trends;
    const totalIssues = trends.reduce((sum, trend) => sum + trend.issueFoundCount, 0);
    const totalReviews = trends.reduce((sum, trend) => sum + trend.totalReviews, 0);
    return totalReviews > 0 ? (totalIssues / totalReviews) * 100 : 0;
  }

  getTotalReviews(): number {
    if (!this.noodleQuestionTrends() || !this.noodleQuestionTrends()!.trends || this.noodleQuestionTrends()!.trends.length === 0) {
      return 0;
    }
    return this.noodleQuestionTrends()!.trends.reduce((sum, trend) => sum + trend.totalReviews, 0);
  }

  getTotalApproved(): number {
    if (!this.noodleQuestionTrends() || !this.noodleQuestionTrends()!.trends || this.noodleQuestionTrends()!.trends.length === 0) {
      return 0;
    }
    return this.noodleQuestionTrends()!.trends.reduce((sum, trend) => sum + trend.approvedCount, 0);
  }

  getTotalIssues(): number {
    if (!this.noodleQuestionTrends() || !this.noodleQuestionTrends()!.trends || this.noodleQuestionTrends()!.trends.length === 0) {
      return 0;
    }
    return this.noodleQuestionTrends()!.trends.reduce((sum, trend) => sum + trend.issueFoundCount, 0);
  }

  /** Circular Chart Calculations */
  getCircleCircumference(): number {
    return 2 * Math.PI * 45;
  }

  getApprovalDashOffset(): number {
    const circumference = this.getCircleCircumference();
    const percentage = this.getAggregatedApprovalPercentage();
    return circumference * (1 - percentage / 100);
  }

  getIssueDashArray(): string {
    const circumference = this.getCircleCircumference();
    const issuePercentage = this.getAggregatedIssuePercentage();
    const issueLength = circumference * (issuePercentage / 100);
    return `${issueLength} ${circumference}`;
  }

  getIssueDashOffset(): number {
    const circumference = this.getCircleCircumference();
    const approvalPercentage = this.getAggregatedApprovalPercentage();
    return circumference * (1 - approvalPercentage / 100);
  }
  /** Circular Chart Calculations - END */
  /** Aggregated Metrics - END */
}
